// src/pages/ConfigSettings.jsx
// System Configuration Settings Page - Edit system configs by ID

import { useState, useEffect } from 'react'
import { Settings, Save, RefreshCw, AlertCircle, Check, Smartphone } from 'lucide-react'
import toast from 'react-hot-toast'
import SystemConfigService from '../services/systemConfigService'
import AuthService from '../services/authService'

const ConfigSettings = () => {
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState({})
  const [editedValues, setEditedValues] = useState({})
  const [autoSendEBill, setAutoSendEBill] = useState(() => {
    return localStorage.getItem('tezlaa_auto_ebill_enabled') !== 'false'
  })

  const handleToggleAutoEBill = (enabled) => {
    setAutoSendEBill(enabled)
    localStorage.setItem('tezlaa_auto_ebill_enabled', String(enabled))
    toast.success(enabled ? 'Auto E-Bill SMS Enabled' : 'Auto E-Bill SMS Disabled')
  }

  const currentUser = AuthService.getCurrentUser()
  const isSuperAdmin = currentUser?.role?.toLowerCase() === 'super_admin' || currentUser?.role?.toLowerCase() === 'superadmin'

  useEffect(() => {
    fetchConfigs()
  }, [])

  const fetchConfigs = async () => {
    setLoading(true)
    try {
      const response = await SystemConfigService.getAllConfigs()
      if (response?.success && response.data) {
        setConfigs(response.data)
        console.log('✅ System configs fetched:', response.data.length)
        console.log('📋 Config IDs:', response.data.map(c => ({ id: c._id, key: c.key })))
      } else {
        toast.error('Failed to fetch configurations')
      }
    } catch (error) {
      console.error('❌ Error fetching configs:', error)
      toast.error('Failed to load configurations')
    } finally {
      setLoading(false)
    }
  }

  const handleValueChange = (configId, newValue) => {
    setEditedValues(prev => ({
      ...prev,
      [configId]: newValue
    }))
  }

  const handleSave = async (config) => {
    const newValue = editedValues[config._id]
    if (newValue === undefined || newValue === '') {
      toast.error('Please enter a value')
      return
    }

    // Convert value based on dataType
    let convertedValue = newValue
    if (config.dataType === 'number') {
      convertedValue = Number(newValue)
      if (isNaN(convertedValue)) {
        toast.error('Please enter a valid number')
        return
      }
    }

    setSaving(prev => ({ ...prev, [config._id]: true }))
    try {
      const response = await SystemConfigService.updateConfig(
        config._id, 
        { value: convertedValue },
        config.key  // Pass the KEY as fallback
      )

      if (response?.success) {
        toast.success(`${config.key} updated successfully`)
        // Update local state
        setConfigs(prev =>
          prev.map(c =>
            c._id === config._id ? { ...c, value: convertedValue } : c
          )
        )
        // Clear edited value
        setEditedValues(prev => {
          const newEdited = { ...prev }
          delete newEdited[config._id]
          return newEdited
        })
      } else {
        toast.error('Failed to update configuration')
      }
    } catch (error) {
      console.error('❌ Error updating config:', error)
      toast.error(error.message || 'Failed to update configuration')
    } finally {
      setSaving(prev => ({ ...prev, [config._id]: false }))
    }
  }

  const getCurrentValue = (config) => {
    return editedValues[config._id] !== undefined
      ? editedValues[config._id]
      : config.value
  }

  const hasChanges = (config) => {
    return editedValues[config._id] !== undefined && 
           editedValues[config._id] !== config.value
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading configurations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Settings className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Configuration</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage system-wide settings and configurations
                </p>
              </div>
            </div>
            <button
              onClick={fetchConfigs}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Configuration Guidelines</p>
            <p>Make sure to save changes after editing each field. Changes will take effect immediately after saving.</p>
          </div>
        </div>

        {/* POS Auto E-Bill Settings Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Smartphone className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">POS Auto E-Bill SMS Setting</h3>
                <p className="text-xs text-gray-600 mt-1 max-w-xl">
                  When enabled, an E-Receipt SMS will automatically be sent to registered customers immediately upon successful payment.
                  <span className="font-semibold text-rose-600 ml-1">(Excluded for Walk-in Customers)</span>.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleToggleAutoEBill(!autoSendEBill)}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm ${
                autoSendEBill
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 ring-2 ring-emerald-400'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <div className={`w-3 h-3 rounded-full ${autoSendEBill ? 'bg-white animate-pulse' : 'bg-gray-400'}`} />
              <span>{autoSendEBill ? '🟢 AUTO E-BILL ON' : '🔴 AUTO E-BILL OFF'}</span>
            </button>
          </div>
        </div>

        {/* Configurations List */}
        <div className="space-y-4">
          {configs.map((config) => (
            <div
              key={config._id}
              className={`bg-white rounded-lg shadow-sm border transition-all ${
                hasChanges(config)
                  ? 'border-orange-300 bg-orange-50/30'
                  : 'border-gray-200'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {config.key}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        config.dataType === 'number'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {config.dataType}
                      </span>
                      {!config.isEditable && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                          Read Only
                        </span>
                      )}
                      {!isSuperAdmin && config.isEditable && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                          View Only
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      {config.description}
                    </p>

                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Value
                        </label>
                        <input
                          type={config.dataType === 'number' ? 'number' : 'text'}
                          value={getCurrentValue(config)}
                          onChange={(e) => handleValueChange(config._id, e.target.value)}
                          disabled={!config.isEditable || !isSuperAdmin}
                          step={config.dataType === 'number' ? 'any' : undefined}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                            !config.isEditable || !isSuperAdmin
                              ? 'bg-gray-100 cursor-not-allowed'
                              : hasChanges(config)
                              ? 'border-orange-300 focus:ring-orange-500'
                              : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                      </div>

                      {config.isEditable && isSuperAdmin && (
                        <button
                          onClick={() => handleSave(config)}
                          disabled={!hasChanges(config) || saving[config._id]}
                          className={`mt-6 px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                            hasChanges(config)
                              ? 'bg-orange-600 text-white hover:bg-orange-700'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          } disabled:opacity-50`}
                        >
                          {saving[config._id] ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : hasChanges(config) ? (
                            <>
                              <Save className="h-4 w-4" />
                              Save
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4" />
                              Saved
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {config.updatedAt && (
                      <div className="mt-3 text-xs text-gray-500">
                        Last updated: {new Date(config.updatedAt).toLocaleString()}
                        {config.updatedBy && ` by ${config.updatedBy.username}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {configs.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Settings className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No configurations found</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConfigSettings
